import { createSlice, PayloadAction, createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";

import { RootState } from "@/store/store";
import { ChartConfiguration, UserConfiguration, userConfigurationSchema } from "@/types/user-configuration.types";

type UserConfigurationState = UserConfiguration;

const INITIAL_USER_CONFIGURATION_STATE: UserConfigurationState = {
    chartConfigurations: {},
};

const USER_CONFIGURATION_SLICE_NAME = "user-configuration";
const userConfigurationSlice = createSlice({
    name: USER_CONFIGURATION_SLICE_NAME,
    initialState: (): UserConfigurationState => {
        const localStorageValue = localStorage.getItem(USER_CONFIGURATION_SLICE_NAME);

        // Don't parse if the user has no configuration stored in local storage
        if (!localStorageValue) {
            return INITIAL_USER_CONFIGURATION_STATE;
        }

        try {
            return userConfigurationSchema.parse(JSON.parse(localStorageValue));
        } catch (e) {
            // Log an error if there was data but it could not be parsed
            console.warn("Invalid user configuration in localStorage, falling back to default.", e);
            return INITIAL_USER_CONFIGURATION_STATE;
        }
    },
    reducers: {
        setChartConfiguration: (
            state,
            action: PayloadAction<{ label: string; configuration: ChartConfiguration | null }>
        ) => {
            if (action.payload.configuration === null) {
                delete state.chartConfigurations[action.payload.label];
                return;
            }

            state.chartConfigurations[action.payload.label] = action.payload.configuration;
        },
    },
});

// Listener for any actions on the user configuration slice saving the state to local storage
export const userConfigurationLocalStorageSaverMiddleware = createListenerMiddleware();
userConfigurationLocalStorageSaverMiddleware.startListening({
    // Match only actions on the user configuration slice
    matcher: isAnyOf(...Object.values(userConfigurationSlice.actions)),
    effect: (_action, listenerApi) => {
        // Get the new state after the reducer has run
        const state = listenerApi.getState() as RootState;

        // Persist the user configuration part of the state to localStorage
        const userConfigurationState = state.userConfiguration;
        localStorage.setItem(USER_CONFIGURATION_SLICE_NAME, JSON.stringify(userConfigurationState));
    },
});

export const { setChartConfiguration } = userConfigurationSlice.actions;
export const userConfigurationReducer = userConfigurationSlice.reducer;
